import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { DataTable, type Column } from "@/components/ui/data-table";
import { usePersistedSort } from "@/lib/use-persisted-sort";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { JobQuickView } from "@/components/job-quick-view";
import { statusColor, formatDate, cn } from "@/lib/utils";
import {
    Plus,
    Search,
    CalendarDays,
    CheckSquare,
    CheckCircle,
    Receipt,
    Calendar,
    X,
    Loader2,
    List,
    LayoutGrid,
    ChevronDown,
    ChevronRight,
    ArrowRight,
    AlertCircle,
} from "lucide-react";
import { WORKFLOW_STAGES, getStage, type LifecycleStageId } from "@/lib/lifecycle";
import {
    JOB_STATUSES,
    statusLabel,
    assemblyName,
    assemblyShort,
    formatAssets,
    deviceCountMismatch,
    tradeExpectsDevices,
} from "@/lib/vocabulary";

interface Trade {
    id: string;
    code: string;
    name: string;
}

interface JobRow {
    id: string;
    display_number: string | null;
    title: string;
    status: string;
    trade_name: string | null;
    customer_name: string;
    company_name: string;
    location_name: string;
    assigned_to_name: string;
    scheduled_date: string | null;
    created_at: string;
    updated_at: string;
    asset_count?: number;
    asset_types?: string | null;
    /** From GET /api/jobs (Table view). */
    scheduled_start?: string | null;
    site_name?: string | null;
    site_city?: string | null;
}

interface LifecycleStageSummary {
    stage: string;
    count: number;
    jobs: JobRow[];
}

const PAGE_SIZE = 25;
const VIEW_KEY = "jobs-view-mode";

/** Column key → the `?sort=` value /api/jobs accepts (parseJobsSort). Only
 *  columns the server can order are sortable; the list is paged. */
const JOBS_SERVER_SORT: Record<string, string> = {
    title: "title",
    status: "status",
    trade_name: "trade",
    customer_name: "customer",
    assigned_to_name: "technician",
    site_name: "location",
    scheduled_start: "scheduled",
    created_at: "created_at",
};

function stageActionButton(stageId: LifecycleStageId, jobId?: string) {
    const stage = getStage(stageId);
    const labels: Record<string, string> = {
        intake: "Complete Intake",
        ready_to_schedule: "Schedule",
        scheduled: "View Route",
        in_progress: "Track Job",
        needs_review: "Review",
        completed: "View Record",
    };
    return { label: labels[stageId] ?? stage.actionLabel, route: jobId ? `/jobs/${jobId}` : stage.actionRoute };
}

// ─── Lifecycle View ──────────────────────────────────────────────────

function LifecycleView({ initialStage }: { initialStage?: string }) {
    const navigate = useNavigate();
    const [expandedStages, setExpandedStages] = useState<Set<string>>(() => {
        if (initialStage) return new Set([initialStage]);
        return new Set(["intake", "ready_to_schedule", "needs_review", "in_progress"]);
    });

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ["jobs", "lifecycle-summary"],
        queryFn: () => api.get<{ stages: LifecycleStageSummary[] }>("/api/jobs/lifecycle-summary"),
    });

    const toggleStage = (stageId: string) => {
        setExpandedStages((prev) => {
            const next = new Set(prev);
            if (next.has(stageId)) next.delete(stageId);
            else next.add(stageId);
            return next;
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-foreground/80" />
            </div>
        );
    }

    if (isError) {
        return (
            <Card className="p-6">
                <div className="flex flex-col items-center gap-3 text-center py-8">
                    <AlertCircle className="h-8 w-8 text-destructive" />
                    <p className="text-sm font-medium">Failed to load job lifecycle data</p>
                    <p className="text-xs text-foreground/80">
                        Something went wrong loading this page. Refresh to try again — if it keeps happening, let the
                        office know what you were doing.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => refetch()}>
                        Retry
                    </Button>
                </div>
            </Card>
        );
    }

    const stageMap = new Map((data?.stages ?? []).map((s) => [s.stage, s]));

    return (
        <div className="space-y-3">
            {WORKFLOW_STAGES.map((stageId) => {
                const stageDef = getStage(stageId);
                const stageData = stageMap.get(stageId);
                const count = stageData?.count ?? 0;
                const jobs = stageData?.jobs ?? [];
                const isOpen = expandedStages.has(stageId);
                const Icon = stageDef.icon;

                return (
                    <Card key={stageId} className="overflow-hidden">
                        <button
                            type="button"
                            onClick={() => toggleStage(stageId)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/40 transition-colors"
                        >
                            {isOpen ? (
                                <ChevronDown className="h-4 w-4 text-foreground/80 shrink-0" />
                            ) : (
                                <ChevronRight className="h-4 w-4 text-foreground/80 shrink-0" />
                            )}
                            <div
                                className="flex h-7 w-7 items-center justify-center rounded-lg shrink-0"
                                style={{ backgroundColor: `${stageDef.hexColor}18`, color: stageDef.hexColor }}
                            >
                                <Icon className="h-4 w-4" />
                            </div>
                            <span className="font-semibold text-sm">{stageDef.label}</span>
                            <Badge variant="outline" className={cn("text-xs", stageDef.badgeClass)}>
                                {count}
                            </Badge>
                            <div className="flex-1" />
                            {count > 0 && (
                                <span
                                    className="text-xs font-medium inline-flex items-center gap-1 hover:underline"
                                    style={{ color: stageDef.hexColor }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(stageDef.actionRoute);
                                    }}
                                >
                                    {stageDef.actionLabel}
                                    <ArrowRight className="h-3 w-3" />
                                </span>
                            )}
                        </button>

                        {isOpen && (
                            <CardContent className="px-4 pb-3 pt-0">
                                {jobs.length === 0 ? (
                                    <EmptyStage stageId={stageId} />
                                ) : (
                                    <div className="space-y-1">
                                        {jobs.map((job) => {
                                            const action = stageActionButton(stageId, job.id);
                                            return (
                                                <div
                                                    key={job.id}
                                                    className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-secondary/50 cursor-pointer transition-colors group"
                                                    onClick={() => navigate(`/jobs/${job.id}`)}
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <p className="font-medium text-sm truncate">{job.title}</p>
                                                        <p className="text-xs text-foreground/80 truncate">
                                                            {job.customer_name || job.company_name || "—"}
                                                            {job.trade_name ? ` · ${job.trade_name}` : ""}
                                                            {job.assigned_to_name ? ` · ${job.assigned_to_name}` : ""}
                                                            {formatAssets(job.asset_count, job.asset_types ?? null)
                                                                ? ` · ${formatAssets(job.asset_count, job.asset_types ?? null)}`
                                                                : ""}
                                                        </p>
                                                        {/* #823 — a device count the title cannot contradict.
                                                            The hand-typed "(N)" is checked against the linked
                                                            devices; a device-trade job with zero linked says so
                                                            in warn styling instead of vanishing into silence. */}
                                                        {(() => {
                                                            const mm = deviceCountMismatch(job.title, job.asset_count);
                                                            if (mm) {
                                                                return (
                                                                    <p className="text-xs font-medium text-red-600 dark:text-red-400">
                                                                        Title says ({mm.titleSays}) — {mm.attached}{" "}
                                                                        device{mm.attached === 1 ? "" : "s"} attached
                                                                    </p>
                                                                );
                                                            }
                                                            if (
                                                                (job.asset_count ?? 0) === 0 &&
                                                                tradeExpectsDevices(job.trade_name)
                                                            ) {
                                                                return (
                                                                    <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                                                                        0 devices attached — the tech lands with nothing
                                                                        to test
                                                                    </p>
                                                                );
                                                            }
                                                            return null;
                                                        })()}
                                                    </div>
                                                    {job.scheduled_date && (
                                                        <span className="text-xs text-foreground/80 shrink-0">
                                                            {formatDate(job.scheduled_date)}
                                                        </span>
                                                    )}
                                                    <Badge className={statusColor(job.status)} variant="outline">
                                                        {statusLabel(job.status)}
                                                    </Badge>
                                                    <span
                                                        className="text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap"
                                                        style={{ color: stageDef.hexColor }}
                                                    >
                                                        {action.label} →
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        {count > jobs.length && (
                                            <button
                                                type="button"
                                                onClick={() => navigate(stageDef.actionRoute)}
                                                className="text-xs text-primary hover:underline ml-3 mt-1"
                                            >
                                                View all {count} →
                                            </button>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        )}
                    </Card>
                );
            })}
        </div>
    );
}

function EmptyStage({ stageId }: { stageId: LifecycleStageId }) {
    const messages: Record<string, string> = {
        intake: "No new requests. When work comes in from calls, portal, or email, it will appear here.",
        ready_to_schedule: "All jobs are scheduled. The Grid is clear.",
        scheduled: "No jobs are scheduled yet. Assign jobs from The Grid.",
        in_progress: "No techs are working right now. Jobs will show here once started.",
        needs_review: "Nothing needs review. Great work.",
        completed: "No completed jobs yet. They'll appear here once processed.",
    };
    return <p className="text-sm text-foreground/80 py-4 px-3">{messages[stageId] ?? "No items in this stage."}</p>;
}

// ─── Table View (existing) ───────────────────────────────────────────

function TableView() {
    const qc = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get("page")) || 1;
    const search = searchParams.get("q") ?? "";
    const statusFilter = searchParams.get("status") ?? "";
    const tradeFilter = searchParams.get("trade") ?? "";
    const [searchInput, setSearchInput] = useState(search);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [quickViewId, setQuickViewId] = useState<string | null>(null);
    const [focusIdx, setFocusIdx] = useState(-1);

    const { data: tradesData } = useQuery({
        queryKey: ["trades"],
        queryFn: () => api.get<{ trades: Trade[] }>("/api/trades"),
    });

    // Sorted on the server — the list is paged, so a browser sort would only
    // reorder the 25 rows on screen. Remembered per user until they re-sort.
    const sort = usePersistedSort({
        tableId: "jobs.list",
        defaultKey: "created_at",
        defaultDir: "desc",
        validKeys: Object.keys(JOBS_SERVER_SORT),
    });

    const { data, isLoading } = useQuery({
        queryKey: ["jobs", page, search, statusFilter, tradeFilter, sort.sortKey, sort.sortDir],
        // Wait for the saved sort so the list doesn't load twice.
        enabled: sort.loaded,
        queryFn: () => {
            const params = new URLSearchParams({
                limit: String(PAGE_SIZE),
                offset: String((page - 1) * PAGE_SIZE),
                sort: JOBS_SERVER_SORT[sort.sortKey ?? "created_at"] ?? "created_at",
                order: sort.sortDir,
            });
            if (search) params.set("q", search);
            if (statusFilter) params.set("status", statusFilter);
            if (tradeFilter) params.set("trade_id", tradeFilter);
            return api.get<{ jobs: JobRow[]; total: number }>(`/api/jobs?${params}`);
        },
    });

    const jobs = data?.jobs ?? [];
    const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (
                e.target instanceof HTMLInputElement ||
                e.target instanceof HTMLTextAreaElement ||
                e.target instanceof HTMLSelectElement
            )
                return;
            if (e.key === "j") setFocusIdx((i) => Math.min(i + 1, jobs.length - 1));
            else if (e.key === "k") setFocusIdx((i) => Math.max(i - 1, 0));
            else if (e.key === "Enter" && focusIdx >= 0 && focusIdx < jobs.length) setQuickViewId(jobs[focusIdx].id);
            else if (e.key === "Escape") setQuickViewId(null);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [focusIdx, jobs]);

    const toggleSelect = useCallback((id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const selectAll = useCallback(() => setSelectedIds(new Set(jobs.map((j) => j.id))), [jobs]);

    const bulkSchedule = useMutation({
        mutationFn: () => api.post("/api/jobs/bulk-schedule", { jobIds: [...selectedIds] }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["jobs"] });
            setSelectedIds(new Set());
        },
    });
    const bulkClose = useMutation({
        mutationFn: () => api.post("/api/jobs/bulk-close", { jobIds: [...selectedIds] }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["jobs"] });
            setSelectedIds(new Set());
        },
    });
    const bulkBilling = useMutation({
        mutationFn: () => api.post("/api/jobs/bulk-generate-billing", { jobIds: [...selectedIds] }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["jobs"] });
            setSelectedIds(new Set());
        },
    });

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setSearchParams((p) => {
            p.set("q", searchInput);
            p.set("page", "1");
            return p;
        });
    };

    const columns: Column<JobRow>[] = [
        {
            key: "select",
            header: "☐",
            className: "w-10",
            render: (r) => (
                <input
                    type="checkbox"
                    checked={selectedIds.has(r.id)}
                    onChange={() => toggleSelect(r.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded"
                />
            ),
        },
        {
            key: "number",
            header: "#",
            className: "w-24",
            render: (r) => (
                <span className="font-mono text-xs text-foreground/80">{r.display_number || r.id.slice(0, 8)}</span>
            ),
        },
        {
            key: "title",
            header: "Title",
            sortable: true,
            render: (r) => <span className="font-medium">{r.title}</span>,
        },
        {
            // #759 (audit J1): the old separate "Stage" column was 100% derived
            // from status (same badge colour, different words) — two columns of
            // width for one fact. One Status column, readable label.
            key: "status",
            header: "Status",
            sortable: true,
            className: "w-36",
            render: (r) => (
                <Badge className={statusColor(r.status)} variant="outline">
                    {statusLabel(r.status)}
                </Badge>
            ),
        },
        {
            key: "trade_name",
            header: "Trade",
            sortable: true,
            className: "w-32",
            render: (r) =>
                r.trade_name ? (
                    <Badge variant="outline">{r.trade_name}</Badge>
                ) : (
                    <span className="text-foreground/80 text-xs">—</span>
                ),
        },
        {
            key: "assets",
            header: "Devices",
            className: "w-32",
            render: (r: any) => {
                // #823: the truth column. A hand-typed "(N)" in the title that
                // disagrees with the linked devices reads red; a device-trade
                // job with zero linked says "0 devices" in amber — never a
                // silent em-dash the office audit read as "normal card".
                const mm = deviceCountMismatch(r.title, r.asset_count);
                if (mm) {
                    return (
                        <span
                            className="text-xs font-medium text-red-600 dark:text-red-400"
                            title={`The title claims (${mm.titleSays}) but ${mm.attached} device${mm.attached === 1 ? " is" : "s are"} attached — fix the devices or the title.`}
                        >
                            title ({mm.titleSays}) ≠ {mm.attached} attached
                        </span>
                    );
                }
                const label = formatAssets(r.asset_count, r.asset_types);
                // #825 m5: the short codes get their plain-English names on
                // hover — a day-one hire shouldn't have to know "DCDA".
                if (label)
                    return (
                        <span
                            className="text-xs text-foreground/80"
                            title={(r.asset_types ?? "")
                                .split(",")
                                .map((t: string) => t.trim())
                                .filter(Boolean)
                                .map((t: string) => `${assemblyShort(t)} = ${assemblyName(t)}`)
                                .join(" · ")}
                        >
                            {label}
                        </span>
                    );
                return tradeExpectsDevices(r.trade_name) ? (
                    <span
                        className="text-xs font-medium text-amber-600 dark:text-amber-400"
                        title="No devices linked — the tech lands with nothing to test. Add them from the job page."
                    >
                        0 devices
                    </span>
                ) : (
                    <span className="text-foreground/80 text-xs">—</span>
                );
            },
        },
        {
            key: "customer_name",
            header: "Customer",
            sortable: true,
            render: (r) => <span>{r.customer_name || r.company_name || "—"}</span>,
        },
        {
            key: "site_name",
            header: "Location",
            sortable: true,
            render: (r) =>
                r.site_name ? (
                    <div className="space-y-0.5">
                        <div className="text-sm">{r.site_name}</div>
                        {r.site_city && <div className="text-[11px] text-foreground/70">{r.site_city}</div>}
                    </div>
                ) : (
                    <span className="text-foreground/50">—</span>
                ),
        },
        {
            key: "scheduled_start",
            header: "Scheduled",
            sortable: true,
            className: "w-32",
            render: (r) =>
                r.scheduled_start ? (
                    <span className="text-xs font-semibold">{formatDate(r.scheduled_start)}</span>
                ) : (
                    <span className="text-foreground/50">—</span>
                ),
        },
        { key: "assigned_to_name", header: "Technician", sortable: true },
        {
            key: "created_at",
            header: "Created",
            className: "w-32",
            sortable: true,
            firstSortDir: "desc",
            render: (r) => <span className="text-foreground/80 text-xs">{formatDate(r.created_at)}</span>,
        },
    ];

    return (
        <>
            {selectedIds.size > 0 && (
                <div className="flex items-center gap-3 rounded-lg border bg-primary/5 border-primary/20 px-4 py-2">
                    <CheckSquare className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">{selectedIds.size} selected</span>
                    {selectedIds.size < jobs.length && (
                        <button onClick={selectAll} className="text-xs text-primary hover:underline">
                            Select all
                        </button>
                    )}
                    <div className="flex gap-2 ml-4">
                        {/* Bulk actions are not yet wired to server routes —
                            disabled with a tooltip until the endpoints land so
                            techs/office can't trigger a no-op. Buttons +
                            mutations are intentionally left in place. */}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span tabIndex={0} className="inline-flex">
                                    <Button size="sm" variant="outline" disabled onClick={() => bulkSchedule.mutate()}>
                                        <Calendar className="h-3.5 w-3.5 mr-1" />
                                        Bulk Schedule
                                    </Button>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>Coming soon — use individual job actions for now.</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span tabIndex={0} className="inline-flex">
                                    <Button size="sm" variant="outline" disabled onClick={() => bulkClose.mutate()}>
                                        <CheckCircle className="h-3.5 w-3.5 mr-1" />
                                        Bulk Close
                                    </Button>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>Coming soon — use individual job actions for now.</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span tabIndex={0} className="inline-flex">
                                    <Button size="sm" variant="outline" disabled onClick={() => bulkBilling.mutate()}>
                                        <Receipt className="h-3.5 w-3.5 mr-1" />
                                        Generate Billing
                                    </Button>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>Coming soon — use individual job actions for now.</TooltipContent>
                        </Tooltip>
                    </div>
                    <button onClick={() => setSelectedIds(new Set())} className="ml-auto p-1 hover:bg-muted rounded">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
                <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-sm">
                    <Input
                        placeholder="Search jobs..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                    <Button type="submit" variant="outline" size="icon">
                        <Search className="h-4 w-4" />
                    </Button>
                </form>
                <Select
                    value={statusFilter}
                    onChange={(e) =>
                        setSearchParams((p) => {
                            p.set("status", e.target.value);
                            p.set("page", "1");
                            return p;
                        })
                    }
                    className="w-40"
                >
                    <option value="">All statuses</option>
                    {JOB_STATUSES.map((s) => (
                        <option key={s} value={s}>
                            {statusLabel(s)}
                        </option>
                    ))}
                </Select>
                <Select
                    value={tradeFilter}
                    onChange={(e) =>
                        setSearchParams((p) => {
                            p.set("trade", e.target.value);
                            p.set("page", "1");
                            return p;
                        })
                    }
                    className="w-40"
                >
                    <option value="">All trades</option>
                    {tradesData?.trades?.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </Select>
            </div>

            <DataTable
                columns={columns}
                data={jobs}
                keyField="id"
                loading={isLoading || !sort.loaded}
                controlledSort={{
                    key: sort.sortKey,
                    dir: sort.sortDir,
                    onChange: (key, firstDir) => {
                        sort.toggleSort(key, firstDir);
                        // Page 3 of a different order is nobody's intent.
                        setSearchParams((p) => {
                            p.set("page", "1");
                            return p;
                        });
                    },
                }}
                onRowClick={(row) => setQuickViewId(row.id)}
                emptyMessage="No jobs scheduled for today — drag a job from the backlog to get started"
                page={page}
                totalPages={totalPages}
                onPageChange={(p) =>
                    setSearchParams((params) => {
                        params.set("page", String(p));
                        return params;
                    })
                }
            />

            {quickViewId && <JobQuickView jobId={quickViewId} onClose={() => setQuickViewId(null)} />}
        </>
    );
}

// ─── Main Page ───────────────────────────────────────────────────────

export default function JobsPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const urlView = searchParams.get("view");
    const initialStage = searchParams.get("stage") ?? undefined;

    const [viewMode, setViewMode] = useState<"lifecycle" | "table">(() => {
        if (urlView === "lifecycle") return "lifecycle";
        if (urlView === "table") return "table";
        try {
            const saved = localStorage.getItem(VIEW_KEY);
            if (saved === "table" || saved === "lifecycle") return saved;
        } catch {
            /* noop */
        }
        return "lifecycle";
    });

    const switchView = (mode: "lifecycle" | "table") => {
        setViewMode(mode);
        try {
            localStorage.setItem(VIEW_KEY, mode);
        } catch {
            /* noop */
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-black tracking-tight text-shell-foreground">Jobs</h1>
                    <p className="text-shell-foreground/80 text-sm">Full lifecycle view of every job.</p>
                </div>
                <div className="flex gap-2 items-center">
                    <div className="flex rounded-lg border overflow-hidden">
                        <button
                            type="button"
                            onClick={() => switchView("lifecycle")}
                            className={cn(
                                "px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5",
                                viewMode === "lifecycle"
                                    ? "bg-primary text-primary-foreground"
                                    : "hover:bg-secondary text-shell-foreground/80 hover:text-foreground",
                            )}
                        >
                            <LayoutGrid className="h-3.5 w-3.5" />
                            Lifecycle
                        </button>
                        <button
                            type="button"
                            onClick={() => switchView("table")}
                            className={cn(
                                "px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5",
                                viewMode === "table"
                                    ? "bg-primary text-primary-foreground"
                                    : "hover:bg-secondary text-shell-foreground/80 hover:text-foreground",
                            )}
                        >
                            <List className="h-3.5 w-3.5" />
                            Table
                        </button>
                    </div>
                    <Button variant="outline" onClick={() => navigate("/jobs/schedule")}>
                        <CalendarDays className="h-4 w-4" />
                        Schedule
                    </Button>
                    <Button onClick={() => navigate("/jobs/new")}>
                        <Plus className="h-4 w-4" />
                        New Job
                    </Button>
                </div>
            </div>

            {viewMode === "lifecycle" ? <LifecycleView initialStage={initialStage} /> : <TableView />}
        </div>
    );
}
